import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { callApiSecure, httpMethods } from '@caplend/cognito-auth';
import * as flags from 'country-flag-icons/react/3x2'; // eslint-disable-line import/no-extraneous-dependencies
import Cognito from '../../../auth/cognito';
import useProgressTracking from '../../../hooks/useProgressTracking';
import SendingGlobe, { DEFAULT_ORIGIN_COORDS } from '../../../components/SendingGlobe';
import ErrorToast from '../../../components/ErrorToast';
import { DocumentType } from '../../../enums';
import * as API from '../../../store/sagas/api';
import { printTransferReceipt, receiptFromSendProgress } from '../../../utils/transferReceipt';
import { getCrossBorderProgressErrorMessage } from '../../../utils/crossBorderHelpers';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { formatNumberWithDecimalLayout, getDecimalLayoutLocale } from '../../../utils/userPreferencesHelpers';

const parseLocation = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split(',').map((part) => Number.parseFloat(part.trim()));
  if (parts.length !== 2 || !parts.every((n) => Number.isFinite(n))) return null;
  const [lat, lng] = parts;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
};

const normalizeStepsPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.steps)) return payload.steps;
  if (Array.isArray(payload?.Steps)) return payload.Steps;
  return [];
};

const cleanSteps = (rawSteps) =>
  (rawSteps || []).map((step, index) => {
    const base = { ...step };
    if (index === 0 && base.description && typeof base.description === 'string') {
      base.description = base.description
        .replace(/\s*<FlagIcon[^>]*\/>\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    return base;
  });

const isPayoutCompletedStep = (step) => String(step?.id || '').toLowerCase() === 'payoutcompleted';

const deriveCompletedFromSteps = (steps) => {
  if (!steps?.length) return false;

  const last = steps[steps.length - 1];
  if (isPayoutCompletedStep(last) && (last.isCurrentStep || last.isStepCompleted || last.isCompleted)) {
    return true;
  }

  if (steps.some((step) => step.isCurrentStep)) return false;

  const lastDone = Boolean(last?.isStepCompleted || last?.isCompleted);
  if (!lastDone) return false;

  if (isPayoutCompletedStep(last)) return true;

  return steps.every((step) => step.isStepCompleted || step.isCompleted);
};

const stepIdsEqual = (left, right) => {
  if (left == null || right == null) return false;
  return String(left).toLowerCase() === String(right).toLowerCase();
};

const resolveActiveStepIndex = (steps, { currentStepId, transferComplete, trackMode }) => {
  if (!steps?.length || transferComplete) return -1;


  if (currentStepId) {
    const fromId = steps.findIndex((step) => stepIdsEqual(step.id, currentStepId));
    if (fromId !== -1) return fromId;
  }

  const fromFlag = steps.findIndex((step) => step.isCurrentStep);
  if (fromFlag !== -1) return fromFlag;

  const firstPending = steps.findIndex((step) => !(step.isStepCompleted || step.isCompleted));
  if (firstPending !== -1) return firstPending;

  if (trackMode) return steps.length - 1;

  return -1;
};

const getStepMessageFromStep = (step) => {
  if (!step) return '';
  const fromField = [step.message, step.Message].find((value) => typeof value === 'string' && value.trim());
  return fromField ? fromField.trim() : '';
};

const getStepMessageText = (step, stepMessages) => {
  if (!step) return '';
  const fromHub = step.id != null && stepMessages.has(step.id) ? String(stepMessages.get(step.id) || '').trim() : '';
  return fromHub || getStepMessageFromStep(step);
};

const resolveImportantUpdateMessage = (steps, { activeStepIndex, stepMessages }) => {
  if (!steps?.length) return '';

  const candidateIndexes = [];
  if (activeStepIndex >= 0) {
    candidateIndexes.push(activeStepIndex);
    for (let index = activeStepIndex - 1; index >= 0; index -= 1) {
      candidateIndexes.push(index);
    }
  }

  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (!candidateIndexes.includes(index)) {
      candidateIndexes.push(index);
    }
  }

  const message = candidateIndexes
    .map((index) => getStepMessageText(steps[index], stepMessages))
    .find((text) => text);

  return message || '';
};

const mergeStepMessagesFromSteps = (stepList, setStepMessages) => {
  if (!stepList?.length) return;

  setStepMessages((prev) => {
    const next = new Map(prev);
    let changed = false;
    stepList.forEach((step) => {
      const text = getStepMessageFromStep(step);
      if (text && step.id != null && next.get(step.id) !== text) {
        next.set(step.id, text);
        changed = true;
      }
    });
    return changed ? next : prev;
  });
};

const formatTransferReference = (requestId) => {
  const compact = String(requestId || '').replace(/-/g, '');
  if (compact.length < 8) return `HP-${compact}`;
  return `HP-${compact.slice(0, 4)}-${compact.slice(-4)}`;
};

const formatAmount = (rawAmount, currencyCode, preferences) => {
  if (rawAmount == null) return null;
  const num = typeof rawAmount === 'number' ? rawAmount : Number.parseFloat(rawAmount);
  if (!Number.isFinite(num)) return null;
  const locale = getDecimalLayoutLocale(preferences);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currencyDisplay: 'symbol',
    }).format(num);
  } catch {
    return `${formatNumberWithDecimalLayout(num, preferences)} ${currencyCode}`;
  }
};

const getStepKey = (step, index) => step?.id ?? step?.stepNumber ?? `step-${index}`;

const getStepLabel = (step, index) => step?.name || step?.label || step?.description || `Step ${index + 1}`;

const VISIBLE_STEP_COUNT = 3;

const getVisibleStepEntries = (steps, getStepStatus, transferComplete) => {
  if (!steps?.length) return [];

  let activeIndex = steps.findIndex((_, index) => getStepStatus(index) === 'active');
  if (activeIndex === -1) {
    if (transferComplete) {
      return steps.slice(-VISIBLE_STEP_COUNT).map((step, offset, arr) => ({
        step,
        index: steps.length - arr.length + offset,
      }));
    }
    activeIndex = steps.findIndex((_, index) => getStepStatus(index) !== 'done');
    if (activeIndex === -1) activeIndex = 0;
  }

  const visible = [];

  for (let index = activeIndex - 1; index >= 0 && visible.length < VISIBLE_STEP_COUNT - 1; index -= 1) {
    if (getStepStatus(index) === 'done') {
      visible.unshift({ step: steps[index], index });
    }
  }

  visible.push({ step: steps[activeIndex], index: activeIndex });

  for (let index = activeIndex + 1; index < steps.length && visible.length < VISIBLE_STEP_COUNT; index += 1) {
    visible.push({ step: steps[index], index });
  }

  return visible;
};

function TransferStepIndicator({ status, label }) {
  const isDone = status === 'done';
  const isActive = status === 'active';

  return (
    <div className='flex items-center gap-3 text-[13px] text-white/[0.92] font-[family-name:var(--font-ui)]'>
      <div
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
          isDone ? 'bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.45)]' : ''
        } ${isActive ? 'border-[1.5px] border-[#22c55e] bg-transparent shadow-[0_0_8px_rgba(34,197,94,0.35)]' : ''} ${
          !isDone && !isActive ? 'border border-white/20 bg-transparent' : ''
        }`}
      >
        {isActive && <div className='h-1.5 w-1.5 rounded-full bg-[#22c55e]' />}
      </div>
      <span>{label}</span>
    </div>
  );
}

TransferStepIndicator.propTypes = {
  status: PropTypes.oneOf(['done', 'active', 'pending']).isRequired,
  label: PropTypes.string.isRequired,
};

function TrackTransferStepIndicator({ status, label }) {
  const isDone = status === 'done';
  const isActive = status === 'active';

  return (
    <div className='flex w-full items-center gap-3 text-[13px] text-white/[0.92] font-[family-name:var(--font-ui)]'>
      <div
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
          isDone ? 'bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.45)]' : ''
        } ${isActive ? 'border-[1.5px] border-[rgba(34,197,94,0.5)] bg-transparent' : ''} ${
          !isDone && !isActive ? 'border border-white/20 bg-transparent' : ''
        }`}
      >
        {isActive && <div className='h-1.5 w-1.5 rounded-full bg-[#22c55e] animate-[hp-pulse_1.2s_ease-in-out_infinite]' />}
      </div>
      <span className='flex-1 text-left'>{label}</span>
    </div>
  );
}

TrackTransferStepIndicator.propTypes = {
  status: PropTypes.oneOf(['done', 'active', 'pending']).isRequired,
  label: PropTypes.string.isRequired,
};

function TrackImportantUpdate({ message }) {
  return (
    <div className='mt-[14px] flex w-full items-start gap-3 border-t border-[rgba(141,189,230,0.09)] pt-[14px] animate-[hp-fade-up_0.4s_ease_both]'>
      <div className='mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[rgba(141,189,230,0.35)]'>
        <div className='h-[5px] w-[5px] rounded-full bg-[#8DBDE6] animate-[hp-pulse_1.5s_ease-in-out_infinite]' />
      </div>
      <div className='min-w-0 flex-1 text-left'>
        <div className='mb-[3px] text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[rgba(141,189,230,0.55)] font-[family-name:var(--font-ui)]'>
          Important Update
        </div>
        <div className='text-[13px] leading-[1.5] text-white/[0.58] font-[family-name:var(--font-ui)]'>{message}</div>
      </div>
    </div>
  );
}

TrackImportantUpdate.propTypes = {
  message: PropTypes.string.isRequired,
};

function DetailRow({ label, value, highlight }) {
  return (
    <div
      className={`flex items-center justify-between px-[18px] py-3 border-b border-[rgba(141,189,230,0.1)] last:border-b-0 ${
        highlight ? 'bg-[rgba(46,180,128,0.06)]' : 'bg-transparent'
      }`}
    >
      <span className='text-xs text-white/50 font-[family-name:var(--font-ui)]'>{label}</span>
      <span
        className={`text-[13.5px] font-medium ${
          highlight ? 'text-[#5fd49b] font-semibold' : 'text-white/[0.95]'
        } ${label === 'Reference' ? 'font-[family-name:var(--font-ui)] tracking-[0.02em] [font-feature-settings:"tnum"]' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  highlight: PropTypes.bool,
};

DetailRow.defaultProps = {
  highlight: false,
};

function SendMoneyTransferProgressStep({
  requestId,
  steps,
  recipientName,
  destinationCountry,
  destinationCountryCode,
  receiveAmount,
  receiveCurrency,
  sendAmount,
  sendCurrency,
  paymentReference,
  paymentPurpose,
  transferFee,
  exchangeRate,
  paymentDocumentFileName,
  onBackHome,
  trackMode = false,
}) {
  const { preferences } = useNumberFormat();
  const hubUrl = `${process.env.REACT_APP_NOTIFICATIONS_HUB_URL}cross-border`;
  const [liveSteps, setLiveSteps] = useState(() => cleanSteps(normalizeStepsPayload(steps)));
  const [apiCompleted, setApiCompleted] = useState(false);
  const [openingInvoice, setOpeningInvoice] = useState(false);
  const [receiptError, setReceiptError] = useState(null);

  useEffect(() => {
    const next = cleanSteps(normalizeStepsPayload(steps));
    if (next.length > 0) {
      setLiveSteps(next);
    }
  }, [steps]);

  const stepsToDisplay = useMemo(() => liveSteps, [liveSteps]);

  const {
    currentStepId,
    isCompleted: hubCompleted,
    hasError,
    errorMessage: hubErrorMessage,
    isHubConnected,
    stepMessages,
    setCurrentStepId,
    setIsCompleted,
    setHasError,
    setErrorMessage,
    setStepMessages,
  } = useProgressTracking({
    hubUrl,
    requestId,
    steps: stepsToDisplay,
    enabled: !!requestId,
    stopWhenTerminal: true,
  });

  useEffect(() => {
    mergeStepMessagesFromSteps(stepsToDisplay, setStepMessages);
  }, [stepsToDisplay, setStepMessages]);

  const applyExecutionStatus = useCallback(
    (data) => {
      const nextSteps = cleanSteps(normalizeStepsPayload(data));
      if (nextSteps.length > 0) {
        setLiveSteps(nextSteps);
        mergeStepMessagesFromSteps(nextSteps, setStepMessages);
        const current = nextSteps.find((step) => step.isCurrentStep);
        if (current?.id) {
          setCurrentStepId(current.id);
        }
      }

      if (data?.isCompleted !== undefined || data?.IsCompleted !== undefined) {
        const completed = Boolean(data?.isCompleted ?? data?.IsCompleted);
        setApiCompleted(completed);
        setIsCompleted(completed);
      } else if (nextSteps.length > 0 && deriveCompletedFromSteps(nextSteps)) {
        setApiCompleted(true);
        setIsCompleted(true);
      }

      const statusError = data?.error || data?.errorMessage || data?.Error || data?.ErrorMessage || '';
      if (data?.hasError !== undefined) {
        setHasError(Boolean(data.hasError));
        if (statusError) setErrorMessage(String(statusError));
      } else if (statusError) {
        setHasError(true);
        setErrorMessage(String(statusError));
      }

      if (nextSteps.some((step) => String(step?.id || '').toLowerCase() === 'complianceblocked')) {
        setHasError(true);
      }
    },
    [setCurrentStepId, setIsCompleted, setHasError, setErrorMessage, setStepMessages],
  );

  const isTrackingTerminal =
    hasError || hubCompleted || apiCompleted || deriveCompletedFromSteps(stepsToDisplay);
  const pollStatusViaHttp = !isHubConnected;

  useEffect(() => {
    if (!requestId || isTrackingTerminal) return undefined;

    let cancelled = false;
    let intervalId = null;
    const fxApiUrl = process.env.REACT_APP_FX_API_URL;
    const pollIntervalMs = 5000;

    const fetchStatus = async () => {
      if (cancelled) return;

      try {
        const data = await callApiSecure(`${fxApiUrl}cross-border/quote/execute/${requestId}`, httpMethods.get, Cognito);
        if (cancelled) return;

        applyExecutionStatus(data);

        const nextSteps = cleanSteps(normalizeStepsPayload(data));
        const reachedTerminalState =
          data?.hasError ||
          data?.HasError ||
          Boolean(data?.error || data?.Error) ||
          data?.isCompleted === true ||
          data?.IsCompleted === true ||
          deriveCompletedFromSteps(nextSteps);

        if (reachedTerminalState && intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      } catch {
        //
      }
    };

    
    fetchStatus();

    if (pollStatusViaHttp) {
      intervalId = window.setInterval(fetchStatus, pollIntervalMs);
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [requestId, applyExecutionStatus, isTrackingTerminal, pollStatusViaHttp]);

  const destinationCoords = parseLocation(destinationCountry?.location);
  const destCode = (destinationCountryCode || destinationCountry?.code || '').toUpperCase();
  const DestFlag = destCode ? flags[destCode] : null;
  const destinationLabel = destinationCountry?.name || destCode || '';

  const receiveDisplay = formatAmount(receiveAmount, receiveCurrency, preferences);
  const sendDisplay = formatAmount(sendAmount, sendCurrency, preferences);
  const referenceDisplay = formatTransferReference(requestId);
  const transferComplete =
    (hubCompleted ||
      apiCompleted ||
      deriveCompletedFromSteps(stepsToDisplay) ||
      isPayoutCompletedStep({ id: currentStepId })) &&
    !hasError;
  const showInTransit = transferComplete && !trackMode;

  const activeStepIndex = useMemo(
    () => resolveActiveStepIndex(stepsToDisplay, { currentStepId, transferComplete, trackMode }),
    [stepsToDisplay, currentStepId, transferComplete, trackMode],
  );

  const getStepStatus = (index) => {
    if (transferComplete) return 'done';

    if (activeStepIndex === -1) {
      const step = stepsToDisplay[index];
      return step?.isStepCompleted || step?.isCompleted ? 'done' : 'pending';
    }

    if (index < activeStepIndex) return 'done';
    if (index === activeStepIndex) return 'active';
    return 'pending';
  };

  const visibleStepEntries = useMemo(
    () => getVisibleStepEntries(stepsToDisplay, getStepStatus, transferComplete),
    [stepsToDisplay, activeStepIndex, transferComplete],
  );

  const importantUpdateMessage = useMemo(
    () => resolveImportantUpdateMessage(stepsToDisplay, { activeStepIndex, stepMessages }),
    [stepsToDisplay, activeStepIndex, stepMessages],
  );

  const renderProgressSteps = () => {
    if (stepsToDisplay.length === 0) {
      return <p className='m-0 pt-2 text-sm text-white/45 font-[family-name:var(--font-ui)]'>Loading transfer status…</p>;
    }

    if (trackMode) {
      return (
        <div className='flex w-full flex-col gap-[10px] pt-2 text-left'>
          {visibleStepEntries.map(({ step, index }) => (
            <TrackTransferStepIndicator
              key={getStepKey(step, index)}
              status={getStepStatus(index)}
              label={getStepLabel(step, index)}
            />
          ))}
          {importantUpdateMessage && <TrackImportantUpdate message={importantUpdateMessage} />}
        </div>
      );
    }

    return (
      <>
        <div className='mx-auto flex w-full max-w-[340px] flex-col items-start gap-2.5 pt-2 text-left'>
          {visibleStepEntries.map(({ step, index }) => (
            <TransferStepIndicator
              key={getStepKey(step, index)}
              status={getStepStatus(index)}
              label={getStepLabel(step, index)}
            />
          ))}
        </div>
        {importantUpdateMessage && (
          <div className='mt-6 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4 text-left'>
            <div className='mb-2 flex items-center gap-2.5'>
              <div className='inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[rgba(141,189,230,0.45)] bg-[rgba(141,189,230,0.12)]'>
                <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='#8DBDE6' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <circle cx='12' cy='12' r='10' />
                  <path d='M12 6v6l4 2' />
                </svg>
              </div>
              <div className='text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8DBDE6] font-[family-name:var(--font-ui)]'>
                Important update
              </div>
            </div>
            <div className='text-[13px] leading-relaxed text-white/70 font-[family-name:var(--font-ui)]'>{importantUpdateMessage}</div>
          </div>
        )}
      </>
    );
  };

  const buildReceiptData = useCallback(
    (statusLabel) =>
      receiptFromSendProgress({
        requestId,
        recipientName,
        destinationCountry,
        sendAmount,
        sendCurrency,
        receiveAmount,
        receiveCurrency,
        paymentReference,
        paymentPurpose,
        transferFee,
        exchangeRate,
        statusLabel,
      }),
    [
      requestId,
      recipientName,
      destinationCountry,
      sendAmount,
      sendCurrency,
      receiveAmount,
      receiveCurrency,
      paymentReference,
      paymentPurpose,
      transferFee,
      exchangeRate,
    ],
  );

  const handleDownloadReceipt = useCallback(
    (statusLabel) => {
      setReceiptError(null);
      try {
        printTransferReceipt(buildReceiptData(statusLabel), preferences);
      } catch (err) {
        setReceiptError(err);
      }
    },
    [buildReceiptData, preferences],
  );

  const handleViewInvoice = useCallback(async () => {
    const fileName = paymentDocumentFileName?.trim();
    if (!fileName || openingInvoice) return;

    setReceiptError(null);
    setOpeningInvoice(true);
    try {
      const accessUrl = await API.getDocumentAccessUrl(DocumentType.CrossBorder, fileName);
      if (accessUrl) {
        window.open(accessUrl, '_blank', 'noopener,noreferrer');
      } else {
        setReceiptError(new Error('Invoice could not be opened.'));
      }
    } catch (err) {
      setReceiptError(err);
    } finally {
      setOpeningInvoice(false);
    }
  }, [openingInvoice, paymentDocumentFileName]);

  const receiptActions = (statusLabel, { showBackHome = false, showDownloadReceipt = true } = {}) => (
    <div className='mt-4 flex flex-wrap items-center justify-center gap-2.5'>
      {showDownloadReceipt && (
        <button
          type='button'
          onClick={() => handleDownloadReceipt(statusLabel)}
          className='inline-flex cursor-pointer items-center gap-2 rounded-lg border border-white/20 bg-transparent px-5 py-3 text-[13px] font-medium text-white/[0.85] font-[family-name:var(--font-ui)]'
        >
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3' />
          </svg>
          Download receipt
        </button>
      )}
      {paymentDocumentFileName?.trim() && (
        <button
          type='button'
          onClick={handleViewInvoice}
          disabled={openingInvoice}
          className='inline-flex cursor-pointer items-center gap-2 rounded-lg border border-white/20 bg-transparent px-5 py-3 text-[13px] font-medium text-white/[0.85] disabled:cursor-not-allowed disabled:opacity-60 font-[family-name:var(--font-ui)]'
        >
          {openingInvoice ? 'Opening…' : 'View invoice'}
        </button>
      )}
      {showBackHome && (
        <button
          type='button'
          onClick={onBackHome}
          className='inline-flex cursor-pointer items-center gap-2 rounded-lg border-0 bg-white px-6 py-3 text-[13px] font-medium tracking-[0.01em] text-[var(--n900)] shadow-[0_4px_16px_rgba(0,0,0,0.25)] font-[family-name:var(--font-ui)]'
        >
          Back to home →
        </button>
      )}
    </div>
  );

  const progressErrorMessage = getCrossBorderProgressErrorMessage(hubErrorMessage, stepsToDisplay);

  const renderErrorOverlay = () => {
    if (!hasError) return null;

    return (
      <div className='absolute inset-0 z-20 flex items-center justify-center bg-[rgba(8,14,24,0.72)] px-6 py-8 backdrop-blur-[2px]'>
        <div className='w-full max-w-[420px] rounded-[14px] border border-white/10 bg-[rgba(15,23,36,0.96)] p-7 text-center shadow-[0_20px_50px_rgba(0,0,0,0.45)]'>
          <p className='m-0 text-sm leading-relaxed text-white/[0.82]'>{progressErrorMessage}</p>
        </div>
      </div>
    );
  };

  if (!destinationCoords) {
    if (!hasError) return null;

    return (
      <div className='relative overflow-hidden rounded-[14px] bg-[var(--n900)] p-8 text-center text-white shadow-[var(--shadow-md)]'>
        {renderErrorOverlay()}
      </div>
    );
  }

  if (showInTransit) {
    return (
      <div className='relative overflow-hidden w-full rounded-[14px] bg-[var(--n900)] text-white shadow-[var(--shadow-md)]'>
        <div className='relative px-7 py-6 text-center'>
          <div className='animate-[hp-fade-up_0.5s_ease_both]'>
            <div className='mb-2 inline-flex items-center gap-3'>
              <div className='inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-[rgba(80,144,206,0.55)] bg-[rgba(29,78,216,0.2)]'>
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#8DBDE6' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <circle cx='12' cy='12' r='10' />
                  <path d='M12 6v6l4 2' />
                </svg>
              </div>
              <h2 className='m-0 font-display text-[30px] font-light leading-[1.1] tracking-[-0.02em] text-white'>In transit</h2>
            </div>
            <p className='m-0 text-sm text-white/60'>
              {sendDisplay && (
                <>
                  {sendDisplay} is on its way to <strong className='font-medium text-white/[0.92]'>{recipientName}</strong>.
                  <br />
                </>
              )}
              We&apos;ll notify you once it settles — usually within 2 hours.
            </p>
          </div>

          <div className='my-2 flex justify-center opacity-40 transition-opacity duration-500'>
            <SendingGlobe
              globeOnly
              destinationCoords={destinationCoords}
              destinationCountryCode={destCode}
              size={280}
              isActive={false}
              originCoords={DEFAULT_ORIGIN_COORDS}
            />
          </div>

          <div className='animate-[hp-fade-up_0.5s_ease_0.15s_both]'>
            <div className='overflow-hidden rounded-xl border border-[rgba(141,189,230,0.14)] bg-white/[0.04] text-left'>
              <DetailRow label='Recipient gets' value={receiveDisplay || '—'} />
              <DetailRow label='You sent' value={sendDisplay || '—'} />
              <DetailRow label='Recipient' value={recipientName || '—'} />
              <DetailRow
                label='Destination'
                value={
                  <span className='inline-flex items-center gap-1'>
                    {DestFlag && (
                      <DestFlag className='inline-block h-[0.9em] w-[1.35em] rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.08)]' title={`${destCode} flag`} />
                    )}
                    {destinationLabel}
                  </span>
                }
              />
              <DetailRow label='Reference' value={paymentReference?.trim() || referenceDisplay} />
              <DetailRow label='Estimated arrival' value='Within 2 hours' highlight />
            </div>

            {receiptActions('Settled', { showBackHome: true })}
          </div>
        </div>
        <style>{`
          @keyframes hp-fade-up {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <ErrorToast error={receiptError} onDismiss={() => setReceiptError(null)} />
        {renderErrorOverlay()}
      </div>
    );
  }

  return (
    <div className='relative overflow-hidden w-full rounded-[14px] bg-[var(--n900)] text-white shadow-[var(--shadow-md)]'>
      <div className={`relative px-8 pb-8 pt-9 text-center${hasError ? ' pointer-events-none select-none' : ''}`}>
          <p className='mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8DBDE6]/70'>In flight</p>
          <h2
            className='m-0 mb-2 font-display text-[32px] font-light leading-tight tracking-[-0.02em] text-white'
            style={{ fontFamily: 'var(--font-display), "Cormorant Garamond", Georgia, serif' }}
          >
            {receiveDisplay && <span>Sending {receiveDisplay}</span>}
            {receiveDisplay && destinationLabel && <span> to </span>}
            {destinationLabel && (
              <span className='whitespace-nowrap'>
                {DestFlag && (
                  <DestFlag
                    className='mx-0.5 inline-block align-[-0.18em] rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.08)]'
                    style={{ width: '1.35em', height: '0.9em' }}
                    title={`${destCode} flag`}
                  />
                )}{' '}
                {destinationLabel}
              </span>
            )}
          </h2>
          <p className='m-0 text-[13.5px] text-white/[0.55]'>
            <span>From Hamburg</span>
            {recipientName && (
              <>
                <span className='mx-1'>→</span>
                <span>{recipientName}</span>
              </>
            )}
          </p>

          <div className='my-3 flex justify-center'>
            <SendingGlobe
              globeOnly
              destinationCoords={destinationCoords}
              destinationCountryCode={destCode}
              size={420}
              isActive={!hasError}
              originCoords={DEFAULT_ORIGIN_COORDS}
            />
          </div>

          {renderProgressSteps()}

          {!trackMode && paymentDocumentFileName?.trim() && receiptActions('In transit', { showDownloadReceipt: false })}
          {trackMode && (
            <style>{`
              @keyframes hp-pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.55; transform: scale(0.85); }
              }
              @keyframes hp-fade-up {
                0% { opacity: 0; transform: translateY(6px); }
                100% { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          )}
      </div>
      <ErrorToast error={receiptError} onDismiss={() => setReceiptError(null)} />
      {renderErrorOverlay()}
    </div>
  );
}

SendMoneyTransferProgressStep.propTypes = {
  requestId: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
      isCurrentStep: PropTypes.bool,
      isStepCompleted: PropTypes.bool,
      isCompleted: PropTypes.bool,
    }),
  ),
  recipientName: PropTypes.string,
  destinationCountry: PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
    location: PropTypes.string,
  }),
  destinationCountryCode: PropTypes.string,
  receiveAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  receiveCurrency: PropTypes.string,
  sendAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sendCurrency: PropTypes.string,
  paymentReference: PropTypes.string,
  paymentPurpose: PropTypes.string,
  transferFee: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exchangeRate: PropTypes.string,
  paymentDocumentFileName: PropTypes.string,
  onBackHome: PropTypes.func.isRequired,
  trackMode: PropTypes.bool,
};

SendMoneyTransferProgressStep.defaultProps = {
  steps: [],
  recipientName: null,
  destinationCountry: null,
  destinationCountryCode: null,
  receiveAmount: null,
  receiveCurrency: 'EUR',
  sendAmount: null,
  sendCurrency: 'EUR',
  paymentReference: '',
  paymentPurpose: '',
  transferFee: null,
  exchangeRate: '',
  paymentDocumentFileName: '',
  trackMode: false,
};

export default SendMoneyTransferProgressStep;
